import { DataHandlerDevice, Device, DeviceRPi, Version, type EidosFirmware } from "luxedo-data"
import { Toast } from "toaster"
import { openUpdateInformerOverlay } from "../../comps/reusable/overlays/device-update"
import { getCurrentUser } from "../../stores/UserStore.svelte"
import { DateTime } from "luxon"

class UpdateMngr {
	lastShownUpdate: string

	deviceList: Array<number> = []
	deviceUpdateIdMap: { [index: number]: string } = {}
	deviceLastUpdateStatus: { [index: number]: boolean } = {}

	constructor() {
		this.listenForNewDevices()
	}

	listenForNewDevices() {
		DataHandlerDevice.addListener(() => {
			if (!this.deviceList || this.deviceList.length === 0) return

			const userDevices = DataHandlerDevice.getMany()
			const newDeviceList = convertDevicesToIdArray(userDevices)
			if (!compareArraysAreEqual(newDeviceList, this.deviceList)) {
				this.resetListeners()
			}
		})
	}

	resetListeners = () => {
		this.clearListeners()
		this.initializeListeners()
	}

	private clearListeners = () => {
		for (const [devId, updateId] of Object.entries(this.deviceUpdateIdMap)) {
			DataHandlerDevice.get(Number(devId))?.removeUpdateListener(updateId)
		}
		this.deviceUpdateIdMap = {}
		this.deviceLastUpdateStatus = {}
	}

	private initializeListeners = () => {
		const allDevices = DataHandlerDevice.getMany().filter((device) => device instanceof DeviceRPi)
		this.deviceList = convertDevicesToIdArray(allDevices)

		for (const device of allDevices) {
			if (!(device instanceof DeviceRPi)) continue

			let updateId = device.addUpdateListener(this.onEidosUpdate)
			this.deviceUpdateIdMap[device.id] = updateId
		}
	}

	onEidosUpdate = async (device: DeviceRPi) => {
		// Keep track somehow of if the device was in "Updating" last time we saw it
		const wasUpdating = this.deviceLastUpdateStatus[device.id] ?? false
		const isCurrentlyUpdating = device.eidos?.status === "UPDATING"

		if (!device.isOnline) {
			this.deviceLastUpdateStatus[device.id] = isCurrentlyUpdating
			return
		}

		// Check if last seen status was "updating"
		// If it was, but it is now online, pull new device data from the datahandler before continuing
		if (wasUpdating && !isCurrentlyUpdating) {
			// Update/rollback just completed - do a pull to ensure we get the latest firmware version, wait a second to catch any delayed status updates
			await new Promise((resolve) => setTimeout(resolve, 3000))
			await DataHandlerDevice.pull([device.id])

			const refreshedDevice = DataHandlerDevice.get(device.id)
			if (!(refreshedDevice instanceof DeviceRPi)) {
				console.error("onEidosUpdate has been called on a non-RPi device", refreshedDevice)
				return
			}
		}

		// Update the tracking status
		this.deviceLastUpdateStatus[device.id] = isCurrentlyUpdating

		// Otherwise continue as normal
		const [availableUpdate, isInBeta] = this.getAvailableUpdate(device)

		const availVersion = new Version(availableUpdate)
		const firmwareVersion = new Version(device.firmwareVersion)
		const lastSeenVersion = new Version(this.lastShownUpdate ?? "0.0.0")

		if (availVersion > firmwareVersion && availVersion > lastSeenVersion) {
			openUpdateInformerOverlay(availableUpdate, isInBeta)
			this.lastShownUpdate = availableUpdate
		}
	}

	getAvailableUpdate = (device: DeviceRPi): [string, boolean] => {
		const isUserBeta = getCurrentUser().hasPriv("beta_opt_in")

		// If availableUpdateBeta is the same as the availableUpdate, the beta update is not actually beta
		const updateBeta = new Version(device.availableUpdateBeta)
		const updateStable = new Version(device.availableUpdate)

		const isUpdateInBeta = updateBeta.is_after(updateStable)
		const updateVer = isUserBeta ? updateBeta : updateStable

		return [updateVer.str, isUpdateInBeta]
	}

	triggerUpdate = (device?: DeviceRPi) => {
		const isUserBeta = getCurrentUser().hasPriv("beta_opt_in")

		const updateDevice = async (dev: DeviceRPi) => {
			return new Promise<void>((res, rej) => {
				dev.update()
				// temporarily disable eidos updates until updating kicks in
				// this should improve responsiveness on the frontend when triggering an update operation
				dev.overrideEidos(
					// @ts-ignore
					{ status: "UPDATING", playback_type: "EMPTY", __TEMP__: true },
					180,
					(eidos: EidosFirmware, d: DeviceRPi) =>
						eidos.status === "UPDATING" || !d.isUpdateAvailable(isUserBeta)
				)

				let timestart = DateTime.now()
				let completionDetected = false

				let interval = setInterval(async () => {
					if (Math.abs(DateTime.now().diff(timestart).as("seconds")) > 180) {
						clearInterval(interval)
						return rej()
					}

					await DataHandlerDevice.pull([dev.id])
					const d = DataHandlerDevice.get(dev.id)

					if (!d.isUpdateAvailable(isUserBeta)) {
						if (!completionDetected) {
							completionDetected = true
							// Update just completed
							clearInterval(interval)

							try {
								// Pull with a delay to catch firmware version updates
								await new Promise((resolve) => setTimeout(resolve, 3000))
								await DataHandlerDevice.pull([dev.id])

								const latest = DataHandlerDevice.get(dev.id)
								if (latest) {
									console.log(
										`Update/rollback completed for ${latest.name}, firmware: ${latest.firmwareVersion}`
									)
									Toast.success(`${latest.name} updated to v${latest.firmwareVersion}`)
								}
							} catch (e) {
								console.warn("Post-update pull failed", e)
							}
							return res()
						}
					}
				}, 2500)
			})
		}

		const devices: Array<DeviceRPi> = device
			? [device]
			: (DataHandlerDevice.getMany().filter(
					(device) =>
						device instanceof DeviceRPi && device.isUpdateAvailable(isUserBeta) && device.isOnline
				) as unknown as Array<DeviceRPi>)

		console.log(`triggering update for`, devices)

		const promises: Array<Promise<void>> = devices.map((d) => updateDevice(d))

		return Promise.all(promises)
	}
}

/**
 * Converts a list of devices to a list of device IDs
 */
function convertDevicesToIdArray(devices: Array<Device>) {
	return devices.map((dev) => dev.id).sort((a, b) => a - b)
}

function compareArraysAreEqual(listA: Array<number>, listB: Array<number>) {
	return JSON.stringify(listA.sort()) === JSON.stringify(listB.sort())
}

export const DeviceUpdateManager = new UpdateMngr()
